import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import type { SavedOrder } from "@/hooks/useOrders";
import { buildRewardsData } from "@/hooks/useRewards";
import { buildTimeline } from "@/pages/order/page";
import type { OrderWithTimeline, RedeemCouponInfo } from "@/pages/order/page";
import { STATUS_CONFIG } from "@/pages/order/page";
import AdminOrderHeader from "./components/AdminOrderHeader";
import AdminOrderActions from "./components/AdminOrderActions";
import AdminOrderItems from "./components/AdminOrderItems";
import AdminOrderCustomer from "./components/AdminOrderCustomer";
import AdminOrderFinancials from "./components/AdminOrderFinancials";
import AdminOrderNotes from "./components/AdminOrderNotes";

export interface AdminOrderData extends OrderWithTimeline {
  createdAt: string;
  redeemCoupon?: RedeemCouponInfo;
  pointsEarned?: number;
  internalNotes?: string;
}

const STATUS_OPTIONS = [
  { value: "pending",   label: "قيد المعالجة", icon: "ri-time-line",            color: "text-amber-500",  bg: "bg-amber-50",   border: "border-amber-200" },
  { value: "confirmed", label: "تم التأكيد",   icon: "ri-checkbox-circle-line", color: "text-green-600",  bg: "bg-green-50",   border: "border-green-200" },
  { value: "shipped",   label: "تم الشحن",     icon: "ri-send-plane-line",      color: "text-indigo-600", bg: "bg-indigo-50",  border: "border-indigo-200" },
  { value: "delivered", label: "تم التوصيل",   color: "text-teal-600",          bg: "bg-teal-50",         border: "border-teal-200",   icon: "ri-truck-line" },
  { value: "cancelled", label: "ملغي",          icon: "ri-close-circle-line",    color: "text-red-600",    bg: "bg-red-50",     border: "border-red-200" },
];

export { STATUS_OPTIONS };

export default function AdminOrderDetailPage() {
  const { ref } = useParams<{ ref: string }>();
  const navigate = useNavigate();

  const [authed, setAuthed] = useState<boolean | null>(null);
  const [order, setOrder] = useState<AdminOrderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [toastType, setToastType] = useState<"success" | "error">("success");
  const [updating, setUpdating] = useState(false);
  const [copied, setCopied] = useState(false);

  // Check admin auth
  useEffect(() => {
    const check = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const isAdmin = session?.user?.user_metadata?.role === "admin";
      setAuthed(isAdmin);
      if (!isAdmin) navigate("/admin");
    };
    check();
  }, [navigate]);

  const showToast = useCallback((msg: string, type: "success" | "error" = "success") => {
    setToast(msg);
    setToastType(type);
    setTimeout(() => setToast(""), 3500);
  }, []);

  const fetchOrder = useCallback(async () => {
    if (!ref) { setError("رقم الطلب غير صحيح"); setLoading(false); return; }
    setLoading(true);
    try {
      const { data, error: fetchError } = await supabase
        .from("orders")
        .select("*")
        .eq("order_ref", ref)
        .maybeSingle();

      if (fetchError || !data) {
        setError("لم يتم العثور على الطلب");
        setLoading(false);
        return;
      }

      const orderData: SavedOrder = {
        orderRef: String(data.order_ref),
        date: String(data.created_at),
        name: String(data.name),
        phone: String(data.phone),
        city: String(data.city),
        address: String(data.address),
        notes: String(data.notes ?? ""),
        paymentLabel: String(data.payment_label),
        items: Array.isArray(data.items) ? data.items : [],
        totalPrice: Number(data.total_price),
        discountCode: String(data.discount_code ?? ""),
        discountPercent: Number(data.discount_percent ?? 0),
        discountAmount: Number(data.discount_amount ?? 0),
        finalPrice: Number(data.final_price),
        status: data.status as SavedOrder["status"],
        customerId: data.customer_id ? String(data.customer_id) : undefined,
      };

      // Fetch redeem coupon
      let redeemCoupon: RedeemCouponInfo | undefined;
      if (data.customer_id) {
        const { data: couponData } = await supabase
          .from("redeem_coupons")
          .select("coupon_code, discount_percent, status, used_at")
          .eq("customer_id", data.customer_id)
          .eq("status", "used")
          .order("used_at", { ascending: false })
          .limit(5);

        const orderTime = new Date(data.created_at).getTime();
        const matched = (couponData ?? []).find((c) => {
          if (!c.used_at) return false;
          return Math.abs(new Date(c.used_at).getTime() - orderTime) < 5 * 60 * 1000;
        });

        if (matched) {
          const discountAmt = Math.round(Number(data.total_price) * (matched.discount_percent / 100));
          redeemCoupon = {
            couponCode: matched.coupon_code,
            discountPercent: matched.discount_percent,
            discountAmount: discountAmt,
            status: matched.status,
            usedAt: matched.used_at,
          };
        }
      }

      const pointsEarned = data.points_earned ? Number(data.points_earned) : undefined;

      setOrder({
        ...orderData,
        createdAt: String(data.created_at),
        timeline: buildTimeline(orderData),
        redeemCoupon,
        pointsEarned,
        internalNotes: data.internal_notes ?? "",
      });
    } catch {
      setError("حدث خطأ أثناء تحميل الطلب");
    } finally {
      setLoading(false);
    }
  }, [ref]);

  useEffect(() => {
    if (authed) fetchOrder();
  }, [authed, fetchOrder]);

  /** Deduct stock for each item when order is delivered */
  const deductStock = async (): Promise<void> => {
    if (!order) return;
    try {
      for (const item of order.items) {
        const productId = item.id;
        if (!productId) continue;

        const { data: productRow } = await supabase
          .from("products")
          .select("id, stock")
          .eq("id", productId)
          .maybeSingle();

        if (!productRow) continue;
        const currentStock = productRow.stock ?? 0;
        const newStock = Math.max(0, currentStock - (item.qty ?? 1));

        await supabase
          .from("products")
          .update({ stock: newStock })
          .eq("id", productId);
      }
    } catch { /* silent */ }
  };

  /** Restore stock when cancelling a delivered order */
  const restoreStock = async (): Promise<void> => {
    if (!order) return;
    // Only restore if order was delivered (stock was deducted)
    if (order.status !== "delivered") return;
    try {
      for (const item of order.items) {
        const productId = item.id;
        if (!productId) continue;

        const { data: productRow } = await supabase
          .from("products")
          .select("id, stock")
          .eq("id", productId)
          .maybeSingle();

        if (!productRow) continue;
        const currentStock = productRow.stock ?? 0;
        await supabase
          .from("products")
          .update({ stock: currentStock + (item.qty ?? 1) })
          .eq("id", productId);
      }
    } catch { /* silent */ }
  };

  /** Refund points when an order is cancelled (if it was previously delivered and had points) */
  const refundPointsOnCancel = async (): Promise<number> => {
    if (!order?.customerId) return 0;
    try {
      const { data: orderRow } = await supabase
        .from("orders")
        .select("id, points_earned")
        .eq("order_ref", order.orderRef)
        .maybeSingle();

      const pointsToRefund = orderRow?.points_earned ?? 0;
      if (pointsToRefund <= 0) return 0;

      const { data: customer } = await supabase
        .from("customers")
        .select("points, total_points_earned")
        .eq("id", order.customerId)
        .maybeSingle();

      const currentPoints = customer?.points ?? 0;
      const currentTotal = customer?.total_points_earned ?? 0;

      await supabase
        .from("customers")
        .update({
          points: Math.max(0, currentPoints - pointsToRefund),
          total_points_earned: Math.max(0, currentTotal - pointsToRefund),
        })
        .eq("id", order.customerId);

      if (orderRow?.id) {
        await supabase
          .from("orders")
          .update({ points_earned: 0 })
          .eq("id", orderRow.id);
      }

      return pointsToRefund;
    } catch {
      return 0;
    }
  };

  const handleStatusChange = async (newStatus: SavedOrder["status"]) => {
    if (!order) return;
    setUpdating(true);
    const { error } = await supabase
      .from("orders")
      .update({ status: newStatus })
      .eq("order_ref", order.orderRef);

    if (!error) {
      // Deduct stock when order is delivered
      if (newStatus === "delivered") {
        await deductStock();
      }

      // Grant points on delivery
      if (newStatus === "delivered" && order.customerId) {
        try {
          const { data: orderRow } = await supabase
            .from("orders")
            .select("id, points_earned")
            .eq("order_ref", order.orderRef)
            .maybeSingle();

          if (orderRow && (!orderRow.points_earned || orderRow.points_earned === 0)) {
            const earned = Math.floor(order.finalPrice / 10);
            if (earned > 0) {
              const { data: customer } = await supabase
                .from("customers")
                .select("points, total_points_earned")
                .eq("id", order.customerId)
                .maybeSingle();

              const currentPoints = customer?.points ?? 0;
              const currentTotal = customer?.total_points_earned ?? 0;
              const newRewards = buildRewardsData(currentPoints + earned, currentTotal + earned);

              await supabase.from("customers").update({
                points: currentPoints + earned,
                total_points_earned: currentTotal + earned,
              }).eq("id", order.customerId);

              await supabase.from("orders").update({ points_earned: earned }).eq("id", orderRow.id);
              showToast(`مُنح ${earned} نقطة للعميل (${newRewards.tierLabel}) ✓`);
            }
          }
        } catch { /* silent */ }
      }

      // Refund points + restore stock on cancellation
      if (newStatus === "cancelled") {
        try {
          const refunded = await refundPointsOnCancel();
          if (refunded > 0) {
            showToast(`تم استرجاع ${refunded} نقطة من العميل ${order.name} بسبب الإلغاء`);
          }
          await restoreStock();
        } catch { /* silent */ }
      }

      const label = STATUS_OPTIONS.find(s => s.value === newStatus)?.label ?? newStatus;
      showToast(`تم تحديث الحالة إلى "${label}" ✓`);
      await fetchOrder();
    } else {
      showToast("حدث خطأ أثناء التحديث", "error");
    }
    setUpdating(false);
  };

  const handleSaveNotes = async (notes: string) => {
    if (!order) return;
    const { error } = await supabase
      .from("orders")
      .update({ internal_notes: notes })
      .eq("order_ref", order.orderRef);

    if (!error) {
      showToast("تم حفظ الملاحظات ✓");
      setOrder(prev => prev ? { ...prev, internalNotes: notes } : prev);
    } else {
      showToast("فشل حفظ الملاحظات", "error");
    }
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}/admin/order/${ref}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
    showToast("تم نسخ رابط الطلب ✓");
  };

  if (authed === null || (authed && loading)) {
    return (
      <div className="min-h-screen bg-[#f5f5f3] flex items-center justify-center" dir="rtl">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-gray-200 border-t-[#1a1a1a] rounded-full animate-spin"></div>
          <p className="text-sm text-gray-400">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!authed) return null;

  return (
    <div className="min-h-screen bg-[#f5f5f3]" dir="rtl">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-line text-base"></i>
            </Link>
            <div className="w-px h-5 bg-gray-200"></div>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 flex items-center justify-center bg-[#1a1a1a] rounded-lg">
                <i className="ri-file-list-3-line text-white text-xs"></i>
              </div>
              <span className="font-bold text-[#1a1a1a] text-sm">
                تفاصيل الطلب
                {order && <span className="text-gray-400 font-normal mr-1">#{order.orderRef}</span>}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Share link button */}
            <button
              onClick={handleCopyLink}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                copied
                  ? "bg-green-50 border border-green-200 text-green-700"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-600"
              }`}
            >
              <i className={`text-sm ${copied ? "ri-check-line" : "ri-links-line"}`}></i>
              <span className="hidden sm:block">{copied ? "تم النسخ!" : "نسخ الرابط"}</span>
            </button>

            {/* View customer order page */}
            {order && (
              <a
                href={`/order/${order.orderRef}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-external-link-line text-sm"></i>
                <span className="hidden sm:block">صفحة العميل</span>
              </a>
            )}

            <Link
              to="/admin"
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-[#1a1a1a] hover:bg-[#333] text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-dashboard-3-line text-sm"></i>
              <span className="hidden sm:block">لوحة التحكم</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-6">
        {error ? (
          <div className="flex flex-col items-center justify-center py-32 text-center gap-5">
            <div className="w-20 h-20 flex items-center justify-center bg-red-50 rounded-2xl">
              <i className="ri-error-warning-line text-4xl text-red-400"></i>
            </div>
            <div>
              <h2 className="font-bold text-[#1a1a1a] text-lg mb-1">{error}</h2>
              <p className="text-gray-400 text-sm">تحقق من رقم الطلب وحاول مجدداً</p>
            </div>
            <Link
              to="/admin"
              className="px-6 py-3 bg-[#1a1a1a] text-white font-bold rounded-xl text-sm cursor-pointer whitespace-nowrap"
            >
              العودة للوحة التحكم
            </Link>
          </div>
        ) : order ? (
          <div className="flex flex-col gap-5">

            {/* Share Banner */}
            <div className="bg-white border border-gray-100 rounded-2xl px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-[#1a1a1a]/5 rounded-xl flex-shrink-0">
                  <i className="ri-share-line text-[#1a1a1a] text-sm"></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-[#1a1a1a]">رابط مشاركة الطلب مع الفريق</p>
                  <p className="text-xs text-gray-400 mt-0.5 font-mono truncate max-w-xs">
                    {window.location.origin}/admin/order/{ref}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={handleCopyLink}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                    copied
                      ? "bg-green-50 border border-green-200 text-green-700"
                      : "bg-[#1a1a1a] text-white hover:bg-[#333]"
                  }`}
                >
                  <i className={`text-sm ${copied ? "ri-check-double-line" : "ri-file-copy-line"}`}></i>
                  {copied ? "تم النسخ!" : "نسخ الرابط"}
                </button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`تفاصيل الطلب #${order.orderRef}: ${window.location.origin}/admin/order/${ref}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/30 text-[#25D366] transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-sm"></i>
                  <span className="hidden sm:block">مشاركة واتساب</span>
                </a>
              </div>
            </div>

            {/* Main Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

              {/* Left Column — main info */}
              <div className="lg:col-span-2 flex flex-col gap-5">
                <AdminOrderHeader order={order} />
                <AdminOrderItems order={order} />
                <AdminOrderFinancials order={order} />
              </div>

              {/* Right Column — actions + customer */}
              <div className="flex flex-col gap-5">
                <AdminOrderActions
                  order={order}
                  onStatusChange={handleStatusChange}
                  updating={updating}
                  onToast={showToast}
                />
                <AdminOrderCustomer order={order} />
                <AdminOrderNotes
                  order={order}
                  onSave={handleSaveNotes}
                />
              </div>
            </div>
          </div>
        ) : null}
      </main>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <div className={`text-white text-sm px-5 py-3 rounded-xl flex items-center gap-2 ${
            toastType === "error" ? "bg-red-500" : "bg-[#1a1a1a]"
          }`}>
            <i className={toastType === "error" ? "ri-error-warning-line text-red-200" : "ri-checkbox-circle-line text-green-400"}></i>
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}
